import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ReportFilterBar from "./ReportFilterBar";
import ReportTable from "./ReportTable";
import Pagination from "../Pagination/Pagination";
import { getPaymentReport } from "../../api/reports";
import { formatDateIST, formatDateTimeIST, formatINR, sumBy } from "./reportUtils";

const PaymentGatewayReport = () => {
  const [, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState({
    from: "",
    to: "",
    q: "",
    page: 1,
    limit: 10,
    sortBy: "status",
    sortOrder: "desc",
    tab: "payments",
    method: "",
    status: "",
  });

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [reportTotals, setReportTotals] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [error, setError] = useState("");

  useEffect(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    let cancelled = false;

    const fetchPaymentReport = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await getPaymentReport(filters);
        if (cancelled) return;

        const reportRows = response?.data?.rows || response?.rows || [];
        setRows(reportRows);
        setReportTotals(response?.data?.totals || response?.totals || {});
        setPagination(
          response?.pagination || {
            page: filters.page,
            limit: filters.limit,
            total: reportRows.length,
            totalPages: 1,
          },
        );
      } catch (fetchError) {
        if (cancelled) return;
        setRows([]);
        setReportTotals({});
        setPagination({ page: 1, limit: filters.limit, total: 0, totalPages: 1 });
        setError(
          fetchError?.response?.data?.message ||
            fetchError.message ||
            "Failed to load payment report.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPaymentReport();
    return () => {
      cancelled = true;
    };
  }, [
    filters.from,
    filters.to,
    filters.q,
    filters.page,
    filters.limit,
    filters.sortBy,
    filters.sortOrder,
    filters.tab,
    filters.method,
    filters.status,
  ]);

  const totals = {
    amount: {
      value: reportTotals.amount ?? sumBy(rows, "amount"),
      format: "inr",
    },
    fee: {
      value: reportTotals.fee ?? sumBy(rows, "fee"),
      format: "inr",
    },
    feeTax: {
      value: reportTotals.feeTax ?? sumBy(rows, "feeTax"),
      format: "inr",
    },
  };

  const columns = [
    {
      key: "orderId",
      label: "Order ID",
      sortable: true,
    },
    {
      key: "paymentId",
      label: "Payment ID",
      sortable: true,
    },
    {
      key: "method",
      label: "Method",
      sortable: true,
    },
    {
      key: "amount",
      label: "Amount",
      sortable: true,
      render: (row) => formatINR(row.amount),
    },
    {
      key: "orderConfirmed",
      label: "Order Confirmed",
      sortable: true,
      render: (row) =>
        row.orderConfirmed ? (
          "Yes"
        ) : row.status === "Captured" ? (
          <span className="report-mismatch">Payment Captured / Order Not Confirmed</span>
        ) : (
          "No"
        ),
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (row) => <span>{row.status}</span>,
    },
  ];

  return (
    <div className="container-fluid p-4 report-page">
      <div className="d-flex justify-content-end mb-3">
        <button type="button" className="btn btn-success" onClick={() => {}}>
          Export CSV
        </button>
      </div>

      <div className="card p-3">
        <ReportFilterBar
          filters={filters}
          onChange={setFilters}
          onReset={setFilters}
          searchPlaceholder="Search order or payment ID..."
          filterSlotInline
          filterSlot={
            <>
              <div className="col-12 col-lg-4">
                <label htmlFor="payment-status" className="form-label">
                  Status
                </label>

                <select
                  id="payment-status"
                  className="form-select"
                  value={filters.status}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      status: e.target.value,
                      page: 1,
                    }))
                  }
                >
                  <option value="">All Status</option>
                  <option value="PAYMENT_PENDING">Payment Pending</option>
                  <option value="FAILED">Failed</option>
                  <option value="CAPTURED">Captured</option>
                  <option value="REFUNDED">Refunded</option>
                </select>
              </div>
            </>
          }
        />

        <ReportTable
          columns={columns}
          rows={rows}
          totals={totals}
          loading={loading}
          error={error}
          page={filters.page}
          total={pagination.total ?? rows.length}
          totalPages={pagination.totalPages || 1}
          sortBy={filters.sortBy}
          sortOrder={filters.sortOrder}
          onSort={(sortBy, sortOrder) =>
            setFilters((prev) => ({
              ...prev,
              sortBy,
              sortOrder,
              page: 1,
            }))
          }
          onPageChange={(page) =>
            setFilters((prev) => ({
              ...prev,
              page: Math.min(Math.max(page, 1), pagination.totalPages || 1),
            }))
          }
          rowKey="paymentId"
        />
      </div>

      <Pagination
        totalItems={pagination.total}
        itemsPerPage={pagination.limit}
        currentPage={pagination.page}
        onPageChange={(page) =>
          setFilters((prev) => ({ ...prev, page }))
        }
      />
    </div>
  );
};

export default PaymentGatewayReport;
